/**
 * The Health component's percentage (ADR-073 decision 11, ADR-074 decision 6): what every watcher reads of a
 * character's health. It is 0 only for the dead and 100 only at the maximum, so a living character is never drawn
 * dead and a hurt one never drawn full; exact values reach their own player through `vitals`.
 */

/**
 * The percentage a health carries on the wire.
 *
 * @param health Exact health, 0 to `max`.
 * @param max The maximum, at least 1.
 * @returns 0 at 0, 100 at the maximum, otherwise `floor(100 * health / max)` held from 1 to 99.
 * @example
 * healthPercent(1, 1000);
 * // 1
 */
export function healthPercent(health: number, max: number): number {
  if (health <= 0) {
    return 0;
  }
  if (health >= max) {
    return 100;
  }
  return Math.min(99, Math.max(1, Math.floor((100 * health) / max)));
}

/**
 * Health as the page shows it: the current health, the full maximum it is shown against, and the part of that maximum
 * a penalty locks away (ADR-079's amendment of 2026-10-03 for P2.2-T16).
 */
export interface HealthShown {
  readonly current: number;
  readonly full: number;
  readonly locked: number;
}

/**
 * The numbers a health bar shows under a maximum-health penalty: the current health over the unpenalised maximum, with
 * the part the penalty removes locked, so a character standing up with Resurrection Sickness reads 98 of 100 rather than
 * 98 of 98. The full maximum is the level's pool, used only while the bearer's effects lower its maximum and only when
 * it is larger than the maximum the server sent; otherwise the served maximum stands with nothing locked (no penalty, a
 * buff that raises it, or a level not yet known). When a penalised character levels up and its new level reaches the
 * page before the `vitals` carrying the new maximum, the new pool meets the old maximum and the locked part overshoots
 * for that one snapshot: an accepted one-frame transient, gone with the next `vitals`.
 *
 * @param health The current health from `vitals`.
 * @param maxHealth The maximum from `vitals`, the penalised one.
 * @param pool The level's health pool, or null when the level is not known.
 * @param maxHealthPercent The bearer's effects' summed `maxHealth` modifier, in whole percents.
 * @returns The current health held from 0 to `maxHealth`, the full maximum and the locked amount.
 * @example
 * healthShown(98, 98, 100, -2);
 * // { current: 98, full: 100, locked: 2 }
 */
export function healthShown(health: number, maxHealth: number, pool: number | null, maxHealthPercent: number): HealthShown {
  const current = Math.min(maxHealth, Math.max(0, health));
  if (maxHealthPercent < 0 && pool !== null && pool > maxHealth) {
    return { current, full: pool, locked: pool - maxHealth };
  }
  return { current, full: maxHealth, locked: 0 };
}
